// pages/ProfilePage.js
import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useAuthState } from '../hooks/useAuthState'
import { usePasswordValidation } from '../hooks/usePasswordValidation'
import {
  getFirestore,
  collection,
  query,
  where,
  getDocs,
  doc,
  updateDoc,
  limit
} from 'firebase/firestore'
import {
  getAuth,
  updatePassword,
  updateProfile,
  reauthenticateWithCredential,
  EmailAuthProvider
} from 'firebase/auth'
import { app } from '../firebase'
import toast from 'react-hot-toast'
import PersonIcon from '@mui/icons-material/Person'
import LockIcon from '@mui/icons-material/Lock'
import BarChartIcon from '@mui/icons-material/BarChart'
import BusinessIcon from '@mui/icons-material/Business'
import EmailIcon from '@mui/icons-material/Email'
import BadgeIcon from '@mui/icons-material/Badge'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import MenuBookIcon from '@mui/icons-material/MenuBook'
import VisibilityIcon from '@mui/icons-material/Visibility'
import ThumbUpIcon from '@mui/icons-material/ThumbUp'
import ThumbDownIcon from '@mui/icons-material/ThumbDown'
import ChatIcon from '@mui/icons-material/Chat'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import PasswordRequirements from '../components/PasswordRequirements'
import './ProfilePage.css'

function ProfilePage () {
  const { user } = useAuth()
  const { company, companyId, userRole } = useAuthState()
  const [loading, setLoading] = useState(true)
  const [savingProfile, setSavingProfile] = useState(false)
  const [changingPassword, setChangingPassword] = useState(false)
  const [error, setError] = useState(null)

  const db = getFirestore(app)
  const auth = getAuth(app)

  // Profile Data
  const [displayName, setDisplayName] = useState('')
  const [accountCreatedAt, setAccountCreatedAt] = useState(null)
  const [lastLoginAt, setLastLoginAt] = useState(null)

  // Password Change
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  })

  // Password validation hook
  const {
    passwordStrength,
    updatePasswordStrength,
    validatePassword,
    passwordsMatch
  } = usePasswordValidation()

  // Statistics
  const [stats, setStats] = useState({
    totalContributions: 0,
    totalUsageCount: 0,
    totalLikes: 0,
    totalDislikes: 0,
    totalConversations: 0,
    accountAgeDays: 0,
    topContribution: null
  })

  useEffect(() => {
    console.log('User data changed:', stats)
  }, [stats])

  useEffect(() => {
    if (!user) {
      setLoading(false)
      return
    }

    loadProfileData()
  }, [user, companyId])

  const loadProfileData = async () => {
    try {
      setLoading(true)

      // Set basic user data
      setDisplayName(user.displayName || user.email)

      // Get user document for createdAt and lastLoginAt
      if (user.userData) {
        if (user.userData.createdAt) {
          const createdDate = user.userData.createdAt.toDate
            ? user.userData.createdAt.toDate()
            : new Date(user.userData.createdAt)
          setAccountCreatedAt(createdDate)

          // Calculate account age
          const ageInMs = Date.now() - createdDate.getTime()
          const ageInDays = Math.floor(ageInMs / (1000 * 60 * 60 * 24))
          setStats(prev => ({ ...prev, accountAgeDays: ageInDays }))
        }

        if (user.userData.lastLoginAt) {
          const lastLogin = user.userData.lastLoginAt.toDate
            ? user.userData.lastLoginAt.toDate()
            : new Date(user.userData.lastLoginAt)
          setLastLoginAt(lastLogin)
        }
      }

      // Load statistics if user has company
      if (companyId) {
        await loadStatistics()
      }

      setLoading(false)
    } catch (err) {
      console.error('Error loading profile:', err)
      setError('Fehler beim Laden des Profils')
      setLoading(false)
    }
  }

  const loadStatistics = async () => {
    try {
      // Load user's knowledge contributions
      const knowledgeRef = collection(db, 'companies', companyId, 'knowledge')
      const knowledgeQuery = query(
        knowledgeRef,
        where('submittedBy', '==', user.email)
      )
      const knowledgeSnapshot = await getDocs(knowledgeQuery)

      let totalUsage = 0
      let totalLikes = 0
      let totalDislikes = 0
      let topContribution = null
      let maxUsage = 0

      knowledgeSnapshot.forEach(doc => {
        console.log('Knowledge Doc:', doc.id, doc.data())
        const data = doc.data()

        // Support both formats: stats object or top-level fields
        const usageCount = data.stats?.usageCount || data.usageCount || 0
        const likes = data.stats?.likes || data.helpfulCount || 0
        const dislikes = data.stats?.dislikes || data.notHelpfulCount || 0

        console.log('Extracted values:', usageCount, likes, dislikes)
        totalUsage += usageCount
        totalLikes += likes
        totalDislikes += dislikes

        // Track top contribution
        if (usageCount > maxUsage) {
          maxUsage = usageCount
          topContribution = {
            title: data.problemTitle,
            usageCount: usageCount,
            deviceName: data.deviceName
          }
        }
      })

      // Load user's conversations (with proper error handling)
      let conversationsCount = 0
      try {
        const conversationsRef = collection(
          db,
          'companies',
          companyId,
          'conversations'
        )
        const conversationsQuery = query(
          conversationsRef,
          where('userId', '==', user.uid),
          limit(100) // Add limit to satisfy Firestore rules
        )
        const conversationsSnapshot = await getDocs(conversationsQuery)
        conversationsCount = conversationsSnapshot.size
      } catch (convError) {
        console.error('Error loading conversations (non-critical):', convError)
        // Continue without conversations count
      }

      // Update stats even if conversations failed
      setStats(prev => ({
        ...prev,
        totalContributions: knowledgeSnapshot.size,
        totalUsageCount: totalUsage,
        totalLikes: totalLikes,
        totalDislikes: totalDislikes,
        totalConversations: conversationsCount,
        topContribution
      }))
    } catch (err) {
      console.error('Error loading statistics:', err)
      // Don't show error, just log it
    }
  }

  const handleSaveProfile = async () => {
    if (!displayName.trim()) {
      toast.error('Display Name darf nicht leer sein')
      return
    }

    setSavingProfile(true)
    setError(null)

    try {
      // Update Firebase Auth profile
      await updateProfile(auth.currentUser, {
        displayName: displayName.trim()
      })

      // Update global users collection
      const userRef = doc(db, 'users', user.uid)
      await updateDoc(userRef, {
        displayName: displayName.trim()
      })

      // Update company users collection if exists
      if (companyId) {
        const companyUserRef = doc(
          db,
          'companies',
          companyId,
          'users',
          user.uid
        )
        await updateDoc(companyUserRef, {
          displayName: displayName.trim()
        })
      }

      toast.success('Profil erfolgreich aktualisiert!')
    } catch (err) {
      console.error('Error saving profile:', err)
      setError('Fehler beim Speichern des Profils')
      toast.error('Fehler beim Speichern')
    } finally {
      setSavingProfile(false)
    }
  }

  const handleChangePassword = async e => {
    e.preventDefault()

    // Validation
    if (!passwordData.currentPassword) {
      toast.error('Bitte aktuelles Passwort eingeben')
      return
    }

    if (!passwordData.newPassword) {
      toast.error('Bitte neues Passwort eingeben')
      return
    }

    // Use password validation hook
    const passwordValidation = validatePassword(passwordData.newPassword)
    if (!passwordValidation.isValid) {
      toast.error(passwordValidation.errors[0])
      return
    }

    if (!passwordsMatch(passwordData.newPassword, passwordData.confirmPassword)) {
      toast.error('Passwörter stimmen nicht überein')
      return
    }

    setChangingPassword(true)
    setError(null)

    try {
      // Reauthenticate user with current password
      const credential = EmailAuthProvider.credential(
        user.email,
        passwordData.currentPassword
      )
      await reauthenticateWithCredential(auth.currentUser, credential)

      // Update password
      await updatePassword(auth.currentUser, passwordData.newPassword)

      // Clear form
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      })

      toast.success('Passwort erfolgreich geändert!')
    } catch (err) {
      console.error('Error changing password:', err)

      if (err.code === 'auth/wrong-password') {
        toast.error('Aktuelles Passwort ist falsch')
      } else if (err.code === 'auth/weak-password') {
        toast.error('Neues Passwort ist zu schwach')
      } else {
        toast.error('Fehler beim Ändern des Passworts')
      }
    } finally {
      setChangingPassword(false)
    }
  }

  const formatDate = date => {
    if (!date) return 'N/A'
    return date.toLocaleDateString('de-DE', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getRoleBadgeClass = role => {
    switch (role) {
      case 'superadmin':
        return 'profile-role-badge superadmin'
      case 'admin':
        return 'profile-role-badge admin'
      case 'moderator':
        return 'profile-role-badge moderator'
      default:
        return 'profile-role-badge user'
    }
  }

  const getRoleLabel = role => {
    switch (role) {
      case 'superadmin':
        return 'Super Admin'
      case 'admin':
        return 'Administrator'
      case 'moderator':
        return 'Moderator'
      default:
        return 'Benutzer'
    }
  }

  if (loading) {
    return (
      <div className='profile-page'>
        <div className='profile-loading'>Lädt Profil...</div>
      </div>
    )
  }

  return (
    <div className='profile-page'>
      <div className='profile-page-header'>
        <h1>Mein Profil</h1>
        <p className='profile-page-subtitle'>
          Verwalten Sie Ihre Account-Einstellungen und sehen Sie Ihre
          Statistiken
        </p>
      </div>

      {error && (
        <div className='profile-error-message'>
          <span>{error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      <div className='profile-grid'>
        {/* Account Info Section */}
        <div className='profile-card'>
          <div className='profile-card-header'>
            <PersonIcon />
            <h2>Account-Informationen</h2>
          </div>

          <div className='profile-info-group'>
            <div className='profile-info-item'>
              <EmailIcon className='profile-info-icon' />
              <div className='profile-info-content'>
                <label>Email</label>
                <span className='profile-info-value'>{user.email}</span>
              </div>
            </div>

            <div className='profile-info-item'>
              <PersonIcon className='profile-info-icon' />
              <div className='profile-info-content'>
                <label>Display Name</label>
                <input
                  type='text'
                  value={displayName}
                  onChange={e => setDisplayName(e.target.value)}
                  placeholder='Ihr Name'
                  disabled={savingProfile}
                />
              </div>
            </div>

            <button
              className='profile-btn-primary'
              onClick={handleSaveProfile}
              disabled={savingProfile || displayName === user.displayName}
            >
              {savingProfile ? 'Speichere...' : 'Name speichern'}
            </button>
          </div>

          <div className='profile-divider'></div>

          <div className='profile-info-group'>
            <div className='profile-info-item'>
              <BusinessIcon className='profile-info-icon' />
              <div className='profile-info-content'>
                <label>Unternehmen</label>
                <span className='profile-info-value'>
                  {company || 'Kein Unternehmen'}
                </span>
              </div>
            </div>

            <div className='profile-info-item'>
              <BadgeIcon className='profile-info-icon' />
              <div className='profile-info-content'>
                <label>Rolle</label>
                <span className={getRoleBadgeClass(userRole)}>
                  {getRoleLabel(userRole)}
                </span>
              </div>
            </div>

            <div className='profile-info-item'>
              <CalendarTodayIcon className='profile-info-icon' />
              <div className='profile-info-content'>
                <label>Account erstellt</label>
                <span className='profile-info-value'>
                  {formatDate(accountCreatedAt)}
                </span>
              </div>
            </div>

            <div className='profile-info-item'>
              <AccessTimeIcon className='profile-info-icon' />
              <div className='profile-info-content'>
                <label>Letzter Login</label>
                <span className='profile-info-value'>
                  {formatDate(lastLoginAt)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Password Change Section */}
        <div className='profile-card'>
          <div className='profile-card-header'>
            <LockIcon />
            <h2>Passwort ändern</h2>
          </div>

          <form
            onSubmit={handleChangePassword}
            className='profile-password-form'
          >
            <div className='profile-form-group'>
              <label htmlFor='currentPassword'>Aktuelles Passwort</label>
              <input
                type='password'
                id='currentPassword'
                value={passwordData.currentPassword}
                onChange={e =>
                  setPasswordData({
                    ...passwordData,
                    currentPassword: e.target.value
                  })
                }
                placeholder='••••••••'
                disabled={changingPassword}
                autoComplete='current-password'
              />
            </div>

            <div className='profile-form-group'>
              <label htmlFor='newPassword'>Neues Passwort</label>
              <input
                type='password'
                id='newPassword'
                value={passwordData.newPassword}
                onChange={e => {
                  const newPassword = e.target.value
                  setPasswordData({
                    ...passwordData,
                    newPassword
                  })
                  updatePasswordStrength(newPassword)
                }}
                placeholder='••••••••'
                disabled={changingPassword}
                autoComplete='new-password'
              />
              {passwordData.newPassword && (
                <PasswordRequirements passwordStrength={passwordStrength} />
              )}
            </div>

            <div className='profile-form-group'>
              <label htmlFor='confirmPassword'>Neues Passwort bestätigen</label>
              <input
                type='password'
                id='confirmPassword'
                value={passwordData.confirmPassword}
                onChange={e =>
                  setPasswordData({
                    ...passwordData,
                    confirmPassword: e.target.value
                  })
                }
                placeholder='••••••••'
                disabled={changingPassword}
                autoComplete='new-password'
              />
            </div>

            <button
              type='submit'
              className='profile-btn-primary'
              disabled={
                changingPassword ||
                !passwordData.currentPassword ||
                !passwordData.newPassword ||
                !passwordData.confirmPassword ||
                !validatePassword(passwordData.newPassword).isValid ||
                !passwordsMatch(passwordData.newPassword, passwordData.confirmPassword)
              }
            >
              {changingPassword ? 'Ändere Passwort...' : 'Passwort ändern'}
            </button>
          </form>
        </div>

        {/* Statistics Section */}
        <div className='profile-card profile-stats-card'>
          <div className='profile-card-header'>
            <BarChartIcon />
            <h2>Meine Statistiken</h2>
          </div>

          <div className='profile-stats-grid'>
            <div className='profile-stat-item'>
              <div className='profile-stat-icon contributions'>
                <MenuBookIcon />
              </div>
              <div className='profile-stat-content'>
                <span className='profile-stat-value'>
                  {stats.totalContributions}
                </span>
                <span className='profile-stat-label'>Wissens-Beiträge</span>
              </div>
            </div>

            <div className='profile-stat-item'>
              <div className='profile-stat-icon usage'>
                <VisibilityIcon />
              </div>
              <div className='profile-stat-content'>
                <span className='profile-stat-value'>
                  {stats.totalUsageCount}
                </span>
                <span className='profile-stat-label'>Gesamt-Aufrufe</span>
              </div>
            </div>

            <div className='profile-stat-item'>
              <div className='profile-stat-icon likes'>
                <ThumbUpIcon />
              </div>
              <div className='profile-stat-content'>
                <span className='profile-stat-value'>{stats.totalLikes}</span>
                <span className='profile-stat-label'>Likes</span>
              </div>
            </div>

            <div className='profile-stat-item'>
              <div className='profile-stat-icon dislikes'>
                <ThumbDownIcon />
              </div>
              <div className='profile-stat-content'>
                <span className='profile-stat-value'>
                  {stats.totalDislikes}
                </span>
                <span className='profile-stat-label'>Dislikes</span>
              </div>
            </div>

            <div className='profile-stat-item'>
              <div className='profile-stat-icon conversations'>
                <ChatIcon />
              </div>
              <div className='profile-stat-content'>
                <span className='profile-stat-value'>
                  {stats.totalConversations}
                </span>
                <span className='profile-stat-label'>Konversationen</span>
              </div>
            </div>

            <div className='profile-stat-item'>
              <div className='profile-stat-icon active-days'>
                <TrendingUpIcon />
              </div>
              <div className='profile-stat-content'>
                <span className='profile-stat-value'>
                  {stats.accountAgeDays}
                </span>
                <span className='profile-stat-label'>Tage aktiv</span>
              </div>
            </div>
          </div>

          {stats.topContribution && (
            <>
              <div className='profile-divider'></div>
              <div className='profile-top-contribution'>
                <h3>Beliebtester Beitrag</h3>
                <div className='profile-top-contribution-card'>
                  <div className='profile-contribution-title'>
                    {stats.topContribution.title}
                  </div>
                  <div className='profile-contribution-meta'>
                    <span>{stats.topContribution.deviceName}</span>
                    <span className='separator'>•</span>
                    <span>{stats.topContribution.usageCount} Aufrufe</span>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default ProfilePage
